import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    MenuItem,
    TextField,
    Typography,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
} from "@mui/material"
import React, { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { getUpdateWorkerModel, updateWorker } from "../../../api/apiCall";
import { useAuth } from "../../../auth/auth";
import { toast } from "react-toastify";

type FormValues = {
    id: string;
    name: string;
    surname: string;
    pts: string;
    username: string;
    phoneNumber: string;
    notes: string;
    email: string;
    suffix: string;
    postCode: string;
    location: string;
    driver: any;
    verified: any;
    tier: number;
    dateOfBirth: string;
    nationality: string;
    gender: string;
    hasDisability: boolean;
    disabilityDetails: string;
    hasCriminalRecord: boolean;
    criminalRecordSpent: boolean;
}

const nationalities = [
    "Albanian",
    "British",
    "Cameroonian",
    "American",
    "Canadian",
    "Ukrainian",
    "Welsh",
    "Fijian",
    "Lithuanian",
    "Australian",
    "Irish",
    "Polish",
    "Romanian",
    "Bulgarian",
    "Italian",
    "Sri Lankan",
    "Norwegian",
    "Malawian",
    "Gambian",
    "Spanish",
    "French",
    "German",
    "Portuguese",
    "Greek",
    "Turkish",
    "Indian",
    "Pakistani",
    "Bangladeshi",
    "Nigerian",
    "Ghanaian",
    "South African",
    "Kenyan",
    "Chinese",
    "Filipino",
    "Brazilian",
    "Mexican",
];

const EditWorker = () => {
    const {state} = useAuth();
    const location = useLocation();
    const { workerId } = location.state || {};
    const [workerData, setworkerData] = useState<any>();
    const [isLoading, setIsLoading] = useState(true);
    const [buttonLoading, setButtonLoading] = useState(false);
    const {
        register,
        handleSubmit,
        control,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormValues>();

    const hasDisabilityValue: boolean = watch("hasDisability");
    const hasCriminalRecordValue: boolean = watch("hasCriminalRecord");

    const getUpdateWorkerData = async () => {
        setIsLoading(true);
        const response = await getUpdateWorkerModel(state.user?.token, workerId);
        setworkerData(response);
        setIsLoading(false);

        if (response) {
            const formattedResponse = {
                ...response,
                dateOfBirth: response?.dateOfBirth ? 
                    response.dateOfBirth.split('T')[0] :
                    "",
                hasDisability: response.hasDisability === true || response.hasDisability === "true" || response.hasDisability === "yes",
                hasCriminalRecord: response.hasCriminalRecord === true || response.hasCriminalRecord === "true" || response.hasCriminalRecord === "yes",
            };
            reset(formattedResponse);
        } else {
            reset(response);
        }
    }

    const handleUpdateWorker = async (data: any) => {
        const trimmedData: any = Object.fromEntries(
            Object.entries(data).map(([key, value]) => [
                key,
                typeof value === 'string' ? value.trim() : value
            ])
        );

        const finalData = {
            ...trimmedData,
            hasDisability: Boolean(trimmedData.hasDisability),
            hasCriminalRecord: Boolean(trimmedData.hasCriminalRecord),
            // criminalRecordSpent: trimmedData.criminalRecordSpent === "yes" || trimmedData.criminalRecordSpent === true
        };

        setButtonLoading(true);
        const response = await updateWorker(state.user?.token, finalData);
        if(response?.status === 200) {
            setTimeout(() => {
                setButtonLoading(false);
                toast.success('Updated successfully');
            }, 500);
        } else {
            setButtonLoading(false);
            toast.error('Something went wrong!');
        }
    };

    useEffect(() => {
        getUpdateWorkerData();
    }, [])

    return (
        <Box sx={{margin: "auto", backgroundColor: "#f2f5fc", padding: 5}}>
            <Box sx={{maxWidth: 650, margin: "auto", backgroundColor: "#fff", padding: 5}}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
                    Edit Worker Account
                </Typography>
                <Box sx={{borderBottom: 1, borderColor: "divider", mb: 2}} />

                {isLoading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 5
                        }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr" },
                                gap: 3,
                                mt: 7
                            }}>
                            <TextField
                                label="National Insurance Number"
                                placeholder="National Insurance Number"
                                variant="outlined"
                                size="small"
                                disabled
                                fullWidth
                                value={workerData?.nationalInsuranceId || ''}
                            />
                            <TextField 
                                label="Name" 
                                variant="outlined" 
                                fullWidth 
                                size="small" 
                                {...register("name")} 
                            />
                            <TextField 
                                label="Surname" 
                                variant="outlined" 
                                fullWidth 
                                size="small" 
                                {...register("surname")} 
                            />
                            
                            <TextField 
                                label="Date of Birth" 
                                type="date"
                                variant="outlined" 
                                fullWidth 
                                size="small" 
                                {...register("dateOfBirth")}
                                slotProps={{
                                    inputLabel: { shrink: true }
                                }}
                            />

                            <Controller
                                name="nationality"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        select
                                        size="small"
                                        label="Nationality"
                                        fullWidth
                                        {...field}
                                    >
                                        {nationalities.map((nat) => (
                                            <MenuItem key={nat} value={nat}>
                                                {nat}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />

                             <Controller
                                name="gender"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        select
                                        size="small"
                                        label="Gender"
                                        fullWidth
                                        {...field}
                                    >
                                        <MenuItem value="">Select Gender</MenuItem>
                                        <MenuItem value="Male">Male</MenuItem>
                                        <MenuItem value="Female">Female</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                        <MenuItem value="Rather not say">Rather not say</MenuItem>
                                    </TextField>
                                )}
                            />

                            <FormControl component="fieldset" required>
                                <FormLabel component="legend">Disability</FormLabel>
                                <Controller
                                    name="hasDisability"
                                    control={control}
                                    render={({ field }) => (
                                        <RadioGroup 
                                            {...field} 
                                            row
                                            value={field.value === true ? "true" : field.value === false ? "false" : ""}
                                            onChange={(e) => field.onChange(e.target.value === "true")}
                                        >
                                            <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                            <FormControlLabel value="false" control={<Radio />} label="No" />
                                        </RadioGroup>
                                    )}
                                />
                            </FormControl>

                            {hasDisabilityValue === true && (
                                <TextField 
                                    label="Please specify disability" 
                                    variant="outlined" 
                                    fullWidth 
                                    size="small" 
                                    multiline
                                    rows={2}
                                    {...register("disabilityDetails")}
                                    required
                                />
                            )}

                            <FormControl component="fieldset" required>
                                <FormLabel component="legend">Criminal Record</FormLabel>
                                <Controller
                                    name="hasCriminalRecord"
                                    control={control}
                                    render={({ field }) => (
                                        <RadioGroup 
                                            {...field} 
                                            row
                                            value={field.value === true ? "true" : field.value === false ? "false" : ""}
                                            onChange={(e) => field.onChange(e.target.value === "true")}
                                        >
                                            <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                            <FormControlLabel value="false" control={<Radio />} label="No" />
                                        </RadioGroup>
                                    )}
                                />
                            </FormControl>

                            {hasCriminalRecordValue === true && (
                                <FormControl component="fieldset" required>
                                    <FormLabel component="legend">Is criminal record spent?</FormLabel>
                                    <Controller
                                        name="criminalRecordSpent"
                                        control={control}
                                        render={({ field }) => (
                                            <RadioGroup
                                                {...field}
                                                row
                                                value={field.value === true ? "true" : field.value === false ? "false" : ""}
                                                onChange={(e) => field.onChange(e.target.value === "true")}>
                                                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                                <FormControlLabel value="false" control={<Radio />} label="No" />
                                            </RadioGroup>
                                        )}
                                    />
                                </FormControl>
                            )}

                            <TextField 
                                label="Username" 
                                variant="outlined" 
                                fullWidth 
                                size="small" 
                                {...register("username")} 
                                disabled 
                            />
                            <TextField 
                                label="PTS" 
                                variant="outlined" 
                                fullWidth 
                                size="small" 
                                {...register("pts")} 
                            />
                            <TextField 
                                label="Phone Number" 
                                variant="outlined" 
                                fullWidth 
                                size="small" 
                                {...register("phoneNumber")} 
                            />
                            <TextField 
                                label="Email" 
                                variant="outlined" 
                                fullWidth 
                                size="small" 
                                {...register("email")} 
                            />
                            <TextField 
                                label="Post Code" 
                                variant="outlined" 
                                fullWidth 
                                size="small" 
                                {...register("postCode")} 
                            />
                            <TextField 
                                label="Location" 
                                variant="outlined" 
                                fullWidth 
                                size="small" 
                                {...register("location")} 
                            />
                            
                            <Controller
                              name="tier"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  select
                                  size="small"
                                  label="Tier"
                                  fullWidth
                                  {...field}
                                  value={field.value ?? 2}
                                  onChange={(e) => field.onChange(Number(e.target.value))}
                                >
                                    <MenuItem value={-1}>No Tier</MenuItem>
                                    <MenuItem value={0}>Active - Currently resourced on live shift</MenuItem>
                                    <MenuItem value={1}>Tier 1 - Not Working / Available {"<"} 24 hours</MenuItem>
                                    <MenuItem value={2}>Tier 2 - Working Available {"<"} 7 days</MenuItem>
                                    <MenuItem value={3}>Tier 3 - Not looking for work currently</MenuItem>
                                    <MenuItem value={4}>Tier 4 - Weekend Worker</MenuItem>
                                    <MenuItem value={5}>Tier 5 - Wrong or missing contact details</MenuItem>
                                </TextField>
                              )}
                            />
                            <TextField 
                                label="Suffix" 
                                variant="outlined" 
                                fullWidth 
                                size="small" 
                                {...register("suffix")} 
                            />
                            <TextField 
                                label="Notes" 
                                variant="outlined" 
                                fullWidth 
                                size="small" 
                                multiline 
                                rows={6} 
                                {...register("notes")} 
                            />
                        </Box>

                        <Typography sx={{ fontSize: 17 }}>
                            Driver:
                            <Controller
                                name="driver"
                                control={control}
                                render={({ field }) => <Checkbox {...field} checked={field.value} />}
                            />
                            Verified:
                            <Controller
                                name="verified"
                                control={control}
                                render={({ field }) => <Checkbox {...field} checked={field.value} />}
                            />
                        </Typography>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mt: 3
                            }}>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                style={{boxShadow: "none", backgroundColor: "#156bc2", width: 160}}
                                disabled={buttonLoading}
                                onClick={handleSubmit(handleUpdateWorker)}
                            >
                                {buttonLoading ? (
                                    <CircularProgress size={25} style={{color: 'white'}} />
                                ) : ('Save')}
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );

};

export default EditWorker;
